import React from "react";
import { Target, Users, Zap, Shield } from "lucide-react";
import { z } from "zod";

const ValuePropCardSchema = z.object({
	icon: z.any(),
	title: z.string(),
	description: z.string(),
	iconBgColor: z.string(),
	iconTextColor: z.string(),
});

const AuthorSchema = z.object({
	name: z.string(),
	title: z.string(),
	avatar: z.string(),
});

const StatSchema = z.object({
	value: z.string(),
	label: z.string(),
});

const ValuePropositionSchema = z.object({
	badgeText: z.string().default("Exclusive Access"),
	header: z.string().default("The Largest"),
	highlightText: z.string().default("PropTech Founders/CEOs Community"),
	description: z
		.string()
		.default(
			"Unlike broad networking platforms, PropTechBuzz offers a focused ecosystem designed exclusively for PropTech innovation leaders. Every connection, conversation, and collaboration is relevant to your industry."
		),
	valueProps: z.array(ValuePropCardSchema).default([
		{
			icon: Target,
			title: "PropTech-Only Focus",
			description:
				"Every member, discussion, and opportunity is PropTech-specific. No noise, just relevant connections that drive your business forward.",
			iconBgColor: "bg-blue-100",
			iconTextColor: "text-blue-800",
		},
		{
			icon: Users,
			title: "Vetted Community",
			description:
				"Join a curated network of verified PropTech founders and leaders. Quality over quantity ensures meaningful connections.",
			iconBgColor: "bg-teal-100",
			iconTextColor: "text-teal-800",
		},
		{
			icon: Zap,
			title: "Early Access",
			description:
				"Get first access to beta tools, exclusive events, and new platform features designed specifically for PropTech founders.",
			iconBgColor: "bg-orange-100",
			iconTextColor: "text-orange-800",
		},
		{
			icon: Shield,
			title: "Global Reach",
			description:
				"Connect with PropTech innovators across 50+ countries. Expand your network internationally with industry-specific focus.",
			iconBgColor: "bg-green-100",
			iconTextColor: "text-green-800",
		},
	]),
	bottomSectionTitle: z.string().default("Why Proptech Founders/CEOs Choose US?"),
	testimonialQuote: z
		.string()
		.default(
			"The signal-to-noise ratio here is incredible. Every conversation leads to actionable insights or valuable partnerships."
		),
	author: AuthorSchema.default({
		name: "Sarah Chen",
		title: "Founder, SmartSpace AI",
		avatar: "SH",
	}),
	stats: z.array(StatSchema).default([
		{ value: "98%", label: "Satisfaction Rate" },
		{ value: "85%", label: "Active Weekly" },
		{ value: "200+", label: "Partnerships Formed" },
		{ value: "50+", label: "Countries" },
	]),
});

type ValuePropositionProps = z.input<typeof ValuePropositionSchema>;

const ValueProposition: React.FC<ValuePropositionProps> = (props = {}) => {
	const data = ValuePropositionSchema.parse(props);

	return (
		<section id="community" className="bg-gray-50 py-24">
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				<div className="mb-16 text-center">
					<span className="mb-4 inline-flex items-center rounded-full bg-blue-100 px-4 py-2 text-sm font-medium text-blue-800">
						<Shield className="mr-2 h-4 w-4" />
						{data.badgeText}
					</span>
					<h2 className="mb-6 text-3xl font-bold text-gray-900 md:text-5xl">
						{data.header}
						<span className="block bg-gradient-to-r from-blue-800 to-teal-600 bg-clip-text text-transparent">
							{data.highlightText}
						</span>
					</h2>
					<p className="mx-auto max-w-3xl text-xl leading-relaxed text-gray-600">
						{data.description}
					</p>
				</div>

				<div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
					{data.valueProps.map((prop, index) => (
						<div
							key={index}
							className="transform rounded-2xl bg-white p-8 shadow-lg transition-all duration-300 hover:-translate-y-2 hover:shadow-xl"
						>
							<div
								className={`mb-6 flex h-16 w-16 items-center justify-center rounded-2xl ${prop.iconBgColor}`}
							>
								<prop.icon className={`h-8 w-8 ${prop.iconTextColor}`} />
							</div>
							<h3 className="mb-4 text-xl font-bold text-gray-900">{prop.title}</h3>
							<p className="leading-relaxed text-gray-600">{prop.description}</p>
						</div>
					))}
				</div>

				<div className="mt-16 rounded-3xl bg-gradient-to-r from-blue-800 to-teal-800 p-8 text-white md:p-12">
					<div className="grid items-center gap-8 md:grid-cols-2">
						<div>
							<h3 className="mb-4 text-2xl font-bold md:text-3xl">{data.bottomSectionTitle}</h3>
							<p className="mb-6 text-lg leading-relaxed text-blue-100">
								&quot;{data.testimonialQuote}&quot;
							</p>
							<div className="flex items-center">
								<div className="mr-4 flex h-12 w-12 items-center justify-center rounded-full bg-orange-500">
									<span className="font-bold text-white">{data.author.avatar}</span>
								</div>
								<div>
									<div className="font-semibold">{data.author.name}</div>
									<div className="text-blue-200">{data.author.title}</div>
								</div>
							</div>
						</div>
						<div className="grid grid-cols-2 gap-6">
							{data.stats.map((stat, index) => (
								<div key={index} className="text-center">
									<div className="mb-2 text-3xl font-bold text-orange-400">{stat.value}</div>
									<div className="text-blue-100">{stat.label}</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};

export default ValueProposition;
